import React, { useState, useEffect, useMemo } from "react"
import { get, post } from "../utils/apiClient"
import toast from "react-hot-toast"
import { DEFAULT_APP_ID, PERSONAL_FAX, FAX_ACTION } from "./../utils/constants"
import {
  isEmpty,
  formatFaxNumber,
  validateFaxNumberOnlyDigit,
  prepareFinalFaxNumber,
} from "./../utils/common"
import Select from "react-select"
import { FaxNumberToolTip } from "./common/faxNumberToolTip"
import { Info } from "lucide-react"
//import CreatableSelect from "react-select/creatable"
import {
  FolderCog,
  SquarePen,
  RefreshCcw,
  Plus,
  Minus,
  Folder,
  FolderCheck,
  FolderGit2,
  Trash2,
  UserStar,
  X,
  TriangleAlert,
} from "lucide-react"

const QueueList = ({
  data,
  appLoading,
  selectedQueue,
  setSelectedQueue,
  loadQueues,
  authUser,
  setSelectedFolderMng,
  allPermissions,
  reloadSubfolderCount,
  setReloadSubfolderCount,
}) => {
  // Track multiple open sections
  const [openParents, setOpenParents] = useState([])
  const [expandedItems, setExpandedItems] = useState({})
  const [loadingSubfolders, setLoadingSubfolders] = useState(null)
  const [showComposePopup, setShowComposePopup] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState({
    userPersonalFax: "",
    queueLevelData: "",
    ownNetIdData: "",
  })

  const handleSendFax = async (data, appId, faxNumber, queueName) => {
    setActionLoading(true)
    try {
      await post("/fax/composeFax", data, {
        appId: appId || DEFAULT_APP_ID,
        faxNumber: faxNumber,
        faxAction: FAX_ACTION.compose,
        originatingFaxNumber: faxNumber,
        queueName: queueName,
        faxType: "",
      })

      setShowComposePopup(null)
      setActionLoading(false)
      toast.success("Fax has been sent successfully", {
        duration: 5000,
      })
    } catch (error) {
      console.error("Failed to send fax", error)

      const message = error.message || "An unexpected error occurred."
      toast.error(message, {
        duration: 5000,
      })
      setActionLoading(false)
    }
  }

  const toggleParent = (parent) => {
    setOpenParents(
      (prev) =>
        prev.includes(parent)
          ? prev.filter((p) => p !== parent) // close
          : [...prev, parent], // open
    )
  }

  const toggleChild = async (parentKey, index, item) => {
    const key = `${parentKey}-${index}`
    const isExpanded = expandedItems[key]?.expanded
    setLoadingSubfolders(key)
    if (!isExpanded) {
      try {
        let subUrl = `/admin/netId-subfolder-associations?netId=${item.netId}`
        if (item.queue) {
          subUrl = `/admin/queue-subfolder-associations?queueName=${item.queue}`
        }

        const sFolders = await get(subUrl)

        const subFolders = [
          ...sFolders.filter((v) => v.subFolder.toLowerCase() !== "trash"),
          ...sFolders.filter((v) => v.subFolder.toLowerCase() === "trash"),
        ]

        setExpandedItems((prev) => ({
          ...prev,
          [key]: { expanded: true, children: subFolders },
        }))

        setLoadingSubfolders(null)
      } catch (err) {
        console.error("Error fetching child data", err)
        setLoadingSubfolders(null)
      }
    } else {
      setExpandedItems((prev) => ({
        ...prev,
        [key]: { ...prev[key], expanded: false },
      }))
    }
    setLoadingSubfolders(null)
  }

  useEffect(() => {
    if (reloadSubfolderCount) {
      updateCopiedFaxCount(
        expandedItems,
        reloadSubfolderCount?.queueName,
        reloadSubfolderCount?.netId,
        reloadSubfolderCount?.subFolder,
        reloadSubfolderCount?.action,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reloadSubfolderCount?.queueName,
    reloadSubfolderCount?.netId,
    reloadSubfolderCount?.subFolder,
    reloadSubfolderCount?.action,
  ])

  const cleanObject = (obj, prefix) => {
    //setSelectedQueue(null)
    return Object.fromEntries(
      Object.entries(obj).filter(([k]) => !k.includes(prefix)),
    )
  }

  // inside your component:
  useEffect(() => {
    setExpandedItems((prev) => cleanObject(prev, "queueLevelData"))
  }, [searchTerm?.queueLevelData])

  useEffect(() => {
    setExpandedItems((prev) => cleanObject(prev, "ownNetIdData"))
  }, [searchTerm?.ownNetIdData])

  useEffect(() => {
    setExpandedItems((prev) => cleanObject(prev, "userPersonalFax"))
  }, [searchTerm?.userPersonalFax])

  function updateCopiedFaxCount(obj, queueName, netId, subFolder, action) {
    try {
      // Deep clone the object so we don't mutate the original
      const newObj = JSON.parse(JSON.stringify(obj))

      // Loop through each queueLevelData
      for (const key in newObj) {
        if (newObj[key] && Array.isArray(newObj[key].children)) {
          newObj[key].children.forEach((child) => {
            if (
              ((child?.queueName &&
                queueName &&
                child?.queueName === queueName) ||
                (child?.netId && netId && child?.netId === netId)) &&
              child.subFolder === subFolder
            ) {
              let count = parseInt(child.copiedFaxCount, 10)
              if (!isNaN(count)) {
                if (action === "copy") {
                  child.copiedFaxCount = String(count + 1)
                } else if (action === "delete") {
                  child.copiedFaxCount = String(count - 1)
                }
              }
            }
          })
        }
      }

      setExpandedItems(newObj) // update expanded items
      setReloadSubfolderCount(null) // reset reload object
      return newObj
    } catch (err) {
      console.warn("updateCopiedFaxCount error:", err)
    }
  }

  const renderList = (parentKey, items, labelKey) => {
    if (!items || items.length === 0) {
      return <div className="no-data">-- Empty --</div>
    }

    // Filter by search for "userPersonalFax"
    let filteredItems =
      (parentKey === "userPersonalFax" ||
        parentKey === "queueLevelData" ||
        parentKey === "ownNetIdData") &&
      searchTerm[parentKey]?.trim() !== ""
        ? items.filter((opt) => {
            const searchV = searchTerm[parentKey].toLowerCase()
            const search = searchV.replace(/[+-]/g, "").trim()
            // const faxNumberToSearch = opt?.faxNumber
            //   ?.replace(/[+-]/g, "")
            //   ?.toLowerCase()

            const faxNumberToSearch =
              opt?.associations?.some((association) =>
                association?.faxNumber
                  ?.replace(/[+-]/g, "")
                  ?.toLowerCase()
                  ?.includes(search),
              ) || opt?.faxNumber?.replace(/[+-]/g, "")?.toLowerCase()

            return (
              (parentKey === "queueLevelData"
                ? opt.queue?.toLowerCase()?.includes(search) ||
                  faxNumberToSearch
                : false) ||
              (parentKey === "ownNetIdData"
                ? opt.netId?.toLowerCase()?.includes(search) ||
                  faxNumberToSearch
                : false) ||
              (parentKey === "userPersonalFax"
                ? opt.netId?.toLowerCase()?.includes(search) ||
                  faxNumberToSearch ||
                  opt.emailAddress?.toLowerCase()?.includes(search)
                : false)
            )
          })
        : items

    return (
      <div className="accordion-section">
        {filteredItems.map((item, idx) => {
          if (item?.faxNumber === PERSONAL_FAX) {
            // remove personal fax for preview only
            return <></>
          }
          const key = `${parentKey}-${idx}`
          const isExpanded = expandedItems[key]?.expanded

          let selectedFolder = false
          if (selectedQueue?.type !== "subfolder") {
            if (
              item?.queue &&
              selectedQueue?.sectionType === "queue" &&
              item?.queue === selectedQueue.queue &&
              parentKey === selectedQueue.parentKey
            ) {
              selectedFolder = true
            }

            if (
              !item?.queue &&
              selectedQueue?.sectionType === "netId" &&
              item?.netId === selectedQueue.netId &&
              parentKey === selectedQueue.parentKey
            ) {
              selectedFolder = true
            }
          }

          const hidefolderCog =
            parentKey === "sharedNetIdData" ||
            parentKey === "userPersonalFax" ||
            (parentKey === "itNetIdData" &&
              authUser.loggedInUserType === "IT" &&
              authUser.netId !== item?.netId)

          return (
            <div key={key}>
              <div className="list-item">
                {preprarePermissions(item.permissions).READ.FOLDER_BROWSE &&
                  parentKey !== "userPersonalFax" && (
                    <>
                      {loadingSubfolders === `${parentKey}-${idx}` ? (
                        <span className="icon-btn loading-icon">
                          <Minus size="16" color="black" />
                        </span>
                      ) : (
                        <span
                          className="icon-btn"
                          onClick={() => {
                            toggleChild(parentKey, idx, item)
                          }}
                          style={{ fontSize: "18px", marginBottom: "4px" }}
                        >
                          {isExpanded ? (
                            <Minus size="16" color="black" />
                          ) : (
                            <Plus size="16" color="black" />
                          )}
                        </span>
                      )}
                    </>
                  )}

                {selectedFolder ? (
                  <span className="user-icon" style={{ paddingLeft: "6px" }}>
                    <FolderGit2 size="18" fill="lightgreen" />
                  </span>
                ) : (
                  <span className="user-icon" style={{ paddingLeft: "6px" }}>
                    <FolderGit2 size="18" color="#3A94D5" />
                  </span>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    marginLeft: "6px",
                  }}
                  onClick={() => {
                    // ************ EXPAND OR QUEUE SELECTION ************

                    if (
                      isEmpty(item?.faxNumber) &&
                      parentKey !== "userPersonalFax" &&
                      isEmpty(item.associations) // FIX for MULTI_FAXNUMBER_BSWH
                    ) {
                      // toggleChild(parentKey, idx, item)
                      if (parentKey !== "sharedNetIdData") {
                        // Subfolder will not open in case of "sharedNetIdData"
                        toggleChild(parentKey, idx, item)
                      }
                    } else {
                      let spreadItem = { ...item }
                      if (!isEmpty(item.associations)) {
                        // FIX for MULTI_FAXNUMBER_BSWH
                        spreadItem = { ...item.associations[0] }
                      }
                      setSelectedQueue({
                        type: "folder",
                        sectionType: item.queue ? "queue" : "netId",
                        parentKey,
                        //...item,
                        ...(parentKey === "userPersonalFax"
                          ? { ...data?.userPersonalFax }
                          : spreadItem),
                        netId: item.netId,
                        subFolders: expandedItems[key]?.children,
                        permissions: preprarePermissions(
                          item.permissions || null,
                        ),
                        associations: item?.associations || null,
                        queue: item.queue,
                      })
                    }

                    // setSelectedQueue({
                    //   type: "folder",
                    //   sectionType: item.queue ? "queue" : "netId",
                    //   parentKey,
                    //   //...item,
                    //   ...(parentKey === "userPersonalFax"
                    //     ? { ...data?.userPersonalFax }
                    //     : { ...item.associations[0] }),
                    //   netId: item.netId,
                    //   subFolders: expandedItems[key]?.children,
                    //   permissions: preprarePermissions(
                    //     item.permissions || null,
                    //   ),
                    //   associations: item.associations,
                    //   queue: item.queue,
                    // })
                  }}
                >
                  <div style={{ display: "flex" }}>
                    <span
                      title={item.faxNumber}
                      style={{ marginBottom: "4px", marginRight: "6px" }}
                    >
                      {item[labelKey] || "(empty)"}{" "}
                    </span>

                    <span title="OWNER">
                      {item.role === "OWNER" ? (
                        <UserStar size={18} color="red" />
                      ) : (
                        ""
                      )}
                    </span>
                  </div>

                  {!hidefolderCog && (
                    <>
                      {preprarePermissions(item.permissions).READ
                        .FOLDER_BROWSE && (
                        <span
                          style={{ paddingRight: "8px" }}
                          onClick={(e) => {
                            e.stopPropagation()

                            setSelectedFolderMng({
                              ...item,
                              sectionType: item.queue ? "queue" : "netId",
                              permissions: preprarePermissions(
                                item.permissions || null,
                              ),
                            })

                            delete expandedItems[key]
                            setExpandedItems(expandedItems)
                            setSelectedQueue(null)
                          }}
                          title="Folder Management"
                        >
                          <FolderCog size={16} color="grey" />
                        </span>
                      )}
                    </>
                  )}

                  {item?.faxNumber === PERSONAL_FAX && (
                    <>
                      <span
                        style={{
                          fontSize: "14px",
                          float: "right",
                          paddingRight: "8px",
                        }}
                        title="Compose Fax"
                        onClick={(e) => {
                          e.stopPropagation()

                          setShowComposePopup({
                            section: "netId",
                            faxNumbers: filteredOwnNetIdFaxNumbers,
                            flgPersonalFax: {
                              appId: item.appId,
                              userId: item.userId,
                            },
                          })
                        }}
                      >
                        <SquarePen size={16} color="grey" />
                      </span>
                    </>
                  )}
                </div>
              </div>

              {isExpanded && expandedItems[key]?.children && (
                <>
                  {expandedItems[key]?.children.length > 0 ? (
                    <ul className="child-list">
                      {expandedItems[key].children.map((child, cIdx) => {
                        if (parentKey !== "queueLevelData") {
                          // FIX for MULTI_FAXNUMBER_BSWH, Trash folder must show for "queueLevelData"
                          if (
                            expandedItems[key].children.length === 1 &&
                            isEmpty(item?.faxNumber) &&
                            child.subFolder === "Trash"
                          ) {
                            return (
                              <div
                                style={{
                                  paddingLeft: "30px",
                                  paddingBottom: "10px",
                                }}
                              >
                                <span>--- Empty ---</span>
                              </div>
                            )
                          } else if (
                            isEmpty(item?.faxNumber) &&
                            child.subFolder === "Trash"
                          ) {
                            return <></>
                          }
                        }

                        let selectedSFolder =
                          selectedQueue?.type === "subfolder" &&
                          child.queueSubfolderId ===
                            selectedQueue?.queueSubfolderId
                            ? true
                            : false

                        if (selectedQueue?.sectionType === "netId") {
                          selectedSFolder =
                            selectedQueue?.type === "subfolder" &&
                            child.netIdSubfolderId ===
                              selectedQueue?.netIdSubfolderId
                              ? true
                              : false
                        }

                        return (
                          <div
                            onClick={() => {
                              setSelectedQueue({
                                type: "subfolder",
                                sectionType: item.queue ? "queue" : "netId",
                                queue: child.queueName,
                                appId: item.appId,
                                permissions: preprarePermissions(
                                  item.permissions || null,
                                ),
                                ...child,
                              })
                            }}
                          >
                            <span
                              className="user-icon"
                              style={{ padding: "3px", cursor: "pointer" }}
                            >
                              {selectedSFolder ? (
                                <span className="user-icon">
                                  {child.subFolder === "Trash" ? (
                                    <Trash2
                                      size="18"
                                      fill="lightgreen"
                                      strokeWidth={1.5}
                                    />
                                  ) : (
                                    <FolderCheck
                                      size="18"
                                      fill="lightgreen"
                                      strokeWidth={1.5}
                                    />
                                  )}
                                </span>
                              ) : (
                                <span className="user-icon">
                                  {child.subFolder === "Trash" ? (
                                    <Trash2
                                      size="18"
                                      color="red"
                                      strokeWidth={1.5}
                                    />
                                  ) : (
                                    <Folder
                                      size="18"
                                      color="#B48A22"
                                      strokeWidth={2}
                                    />
                                  )}
                                </span>
                              )}
                            </span>
                            <li
                              key={cIdx}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              {child?.subFolder}{" "}
                              {child?.copiedFaxCount > 0 && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    marginLeft: "5px",
                                    marginTop: "2px",
                                  }}
                                >
                                  ({child?.copiedFaxCount})
                                </span>
                              )}
                            </li>
                          </div>
                        )
                      })}
                    </ul>
                  ) : (
                    <div style={{ paddingLeft: "30px", paddingBottom: "10px" }}>
                      <span>--- Empty ---</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const preprarePermissions = (queuePermissions = null) => {
    const result = {}

    Object.entries(allPermissions).forEach(([category, permissionsObj]) => {
      result[category] = {}

      Object.keys(permissionsObj).forEach((permKey) => {
        const hasPermission = queuePermissions
          ? queuePermissions?.[category]?.includes(permKey) || false
          : true

        result[category][permKey] = hasPermission
      })
    })

    return result
  }

  const filteredQueueLevelFaxNumbers = []
  data?.queueLevelData?.forEach((q) => {
    q?.associations?.forEach((v) => {
      // FIX for MULTI_FAXNUMBER_BSWH
      const permission = preprarePermissions(v?.permissions || null)
      if (
        permission.WRITE?.FAX_CREATE
        // && v.faxNumber && v.appId // FIX for MULTI_FAXNUMBER_BSWH
      ) {
        filteredQueueLevelFaxNumbers.push({
          faxNumber: v.faxNumber,
          queueOrNetid: q.queue,
          userId: v.userId,
          appId: v.appId,
        })
      }
    })
  })

  const filteredOwnNetIdFaxNumbers = []
  data?.ownNetIdData?.forEach((v) => {
    const permission = preprarePermissions(v.permissions || null)
    if (permission.WRITE?.FAX_CREATE && v.faxNumber && v.appId) {
      filteredOwnNetIdFaxNumbers.push({
        faxNumber: v.faxNumber,
        queueOrNetid: v.netId,
        userId: v.userId,
        appId: v.appId,
      })
    }
  })

  const renderSearchInput = (section) => {
    return (
      <>
        {openParents.includes(section) && (
          <div
            className="search-container"
            style={{
              justifyContent: "space-between",
              padding: "5px",
              margin: "0px",
            }}
          >
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm[section]}
              onChange={(e) =>
                setSearchTerm({
                  ...searchTerm,
                  [section]: e.target.value,
                })
              }
              style={{
                width: "150px",
                height: "10px",
                fontSize: "12px",
                padding: "8px",
                marginLeft: "4px",
                borderBottom: "1px dashed #ccc",
                borderTop: "none",
                borderRight: "none",
                borderLeft: "none",
              }}
            />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {appLoading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message" style={{ fontSize: "0.8em" }}>
            Loading...
          </div>
        </div>
      ) : (
        <>
          {/* {data.queueLevelData.length > 0 && ( */}
          <div className="refresh-icon-container">
            <span
              style={{
                marginLeft: "12px",
                fontSize: "12px",
                margin: "10px",
              }}
            >
              Reload
            </span>
            <span
              title="Click to reload files"
              // className="refresh-button"
              onClick={() => {
                loadQueues()
              }}
              style={{ marginRight: "12px", cursor: "pointer" }}
            >
              <RefreshCcw size={16} color="grey" />
            </span>
          </div>
          {/* )} */}
          <div className="vertical-accordion">
            {/* Queue Level Data */}
            <div className="parent-tab">
              <h3 onClick={() => toggleParent("queueLevelData")}>
                Queues
                {filteredQueueLevelFaxNumbers.length > 0 && (
                  <span
                    style={{ fontSize: "14px", float: "right" }}
                    title="Compose Fax"
                    onClick={(e) => {
                      e.stopPropagation()

                      setShowComposePopup({
                        section: "queue",
                        faxNumbers: filteredQueueLevelFaxNumbers,
                      })
                    }}
                  >
                    <SquarePen size={20} color="black" />
                  </span>
                )}
              </h3>
              {renderSearchInput("queueLevelData")}
              {openParents.includes("queueLevelData") &&
                renderList("queueLevelData", data.queueLevelData, "queue")}
            </div>

            {/* Own NetId Data */}
            <div className="parent-tab">
              <h3 onClick={() => toggleParent("ownNetIdData")}>
                {authUser.loggedInUserType === "IT"
                  ? `User NetIDs`
                  : `Own NetID`}
                {filteredOwnNetIdFaxNumbers.length > 0 && (
                  <span
                    style={{
                      fontSize: "14px",
                      float: "right",
                    }}
                    title="Compose Fax"
                    onClick={(e) => {
                      e.stopPropagation()

                      setShowComposePopup({
                        section: "netId",
                        faxNumbers: filteredOwnNetIdFaxNumbers,
                      })
                    }}
                  >
                    <SquarePen size={20} color="black" />
                  </span>
                )}
              </h3>
              {renderSearchInput("ownNetIdData")}
              {openParents.includes("ownNetIdData") &&
                renderList("ownNetIdData", data.ownNetIdData, "netId")}
            </div>

            {/* {authUser.loggedInUserType === "IT" && (
              <div className="parent-tab">
                <h3 onClick={() => toggleParent("userPersonalFax")}>
                  {`User Personal Fax`}
                </h3>
                {renderSearchInput("userPersonalFax")}
                {openParents.includes("userPersonalFax") &&
                  renderList("userPersonalFax", data.ownNetIdData, "netId")}
              </div>
            )} */}

            {authUser.loggedInUserType === "USER" && (
              <div className="parent-tab">
                <h3
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleParent("sharedNetIdData")
                  }}
                >
                  {authUser.loggedInUserType === "IT"
                    ? `IT NetIDs`
                    : `Shared NetIDs`}

                  {/* {authUser.loggedInUserType !== "IT" && (
                    <span
                      style={{ fontSize: "14px", float: "right" }}
                      title="Compose Fax"
                      onClick={() =>
                        setShowComposePopup({
                          faxNumbers: data.sharedNetIdData.map((v) => ({
                            faxNumber: v.faxNumber,
                            queueOrNetid: v.netId,
                            userId: v.userId,
                          })),
                        })
                      }
                    >
                      <SquarePen size={18} color="grey" />
                    </span>
                  )} */}
                </h3>
                {openParents.includes("sharedNetIdData") &&
                  renderList("sharedNetIdData", data.sharedNetIdData, "netId")}
              </div>
            )}
            {/* {authUser.loggedInUserType === "IT" && (
              <div className="parent-tab">
                <h3 onClick={() => toggleParent("itNetIdData")}>
                  IT NetId{" "}
                  <span
                    style={{ fontSize: "14px", float: "right" }}
                    title="Compose Fax"
                    onClick={() =>
                      setShowComposePopup({
                        faxNumbers: data.itNetIdData.map((v) => ({
                          faxNumber: v.faxNumber,
                          queueOrNetid: v.netId,
                          userId: v.userId,
                        })),
                      })
                    }
                  >
                    <SquarePen size={18} color="grey" />
                  </span>
                </h3>
                {openParents.includes("itNetIdData") &&
                  renderList("itNetIdData", data.itNetIdData, "netId")}
              </div>
            )} */}
          </div>
        </>
      )}

      {showComposePopup && (
        <ComposeForm
          showComposePopup={showComposePopup}
          onSave={handleSendFax}
          actionLoading={actionLoading}
          setActionLoading={setActionLoading}
          onClose={() => {
            setShowComposePopup(null)
          }}
          selectedQueue={selectedQueue}
          // faxNumberAttributes={showComposePopup?.faxNumberAttributes}
          // document={document}
        />
      )}
    </>
  )
}

export default QueueList

const ComposeForm = ({
  showComposePopup,
  onClose,
  onSave,
  actionLoading,
  selectedQueue,
  setActionLoading,
  //selectedFax,
  //faxNumberAttributes,
  //document,
}) => {
  const [faxNumberAttributes, setFaxNumberAttributes] = useState([])
  const [toUserId, setToUserId] = useState(null)
  const [toName, setToName] = useState("")
  const [toCompany, setToCompany] = useState("")
  const [comment1, setComment1] = useState("")
  const [comment2, setComment2] = useState("")
  const [faxNumber, setFaxNumber] = useState(null)
  const [includeCoverPage, setIncludeCoverPage] = useState(true)
  const [fromName, setFromName] = useState("")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  //   const [message, setMessage] = useState(
  //     `Fax Number: ${toUserId}
  // Phone Number:
  // `,
  //   )

  const [errorMessage, setErrorMessage] = useState("")
  const [selectedFiles, setSelectedFiles] = useState([])
  const [destinationFaxNumberLoading, setDestinationFaxNumberLoading] =
    useState(false)
  const [destinationfaxNumbers, setDestinationfaxNumbers] = useState([])
  const [isNoFileAttached, setIsNoFileAttached] = useState(false)

  const fetchDestinationFaxNumbers = async (selectedQueueOrNetId = null) => {
    setDestinationFaxNumberLoading(true)

    let type = "PERSONAL"
    let qString = ""
    if (showComposePopup?.section === "queue" && selectedQueueOrNetId) {
      type = "QUEUE"
      qString = `?queue=${selectedQueueOrNetId}`
    }

    try {
      const faxNumbers = await get(`/phoneBook/${type}${qString}`)

      setDestinationfaxNumbers(faxNumbers)
      setDestinationFaxNumberLoading(false)
    } catch (error) {
      console.error("Failed to fetch fetchFaxNumbers", error)
      setDestinationFaxNumberLoading(false)
    }
  }

  // function getFilteredFaxNumberAttributes(practicefolders) {
  //   const { faxNumberAttributes, customCoverPageFaxNumbers } = practicefolders

  //   // If custom list is empty, return all
  //   if (!customCoverPageFaxNumbers || customCoverPageFaxNumbers.length === 0) {
  //     return faxNumberAttributes
  //   }

  //   // Else, filter based on matching fax_number
  //   return faxNumberAttributes.filter((attr) =>
  //     customCoverPageFaxNumbers.includes(attr.fax_number)
  //   )
  // }

  const handleSubmit = async (e, isConfirmed = false) => {
    e.preventDefault()

    const toUserIdValue = showComposePopup?.flgPersonalFax?.userId
      ? showComposePopup?.flgPersonalFax?.userId
      : toUserId?.value

    if (!toUserIdValue) {
      setErrorMessage("Origination fax number is required")
      return
    }

    if (!faxNumber) {
      setErrorMessage("Destination fax number is required")
      return
    } else {
      const errorMessage = validateFaxNumberOnlyDigit(faxNumber?.trim())

      if (errorMessage) {
        setErrorMessage(errorMessage)
        return
      }
    }

    // if (selectedFiles.length === 0) {
    //   setErrorMessage("Please attach a pdf file to send")
    //   return
    // }

    if (selectedFiles.length > 25) {
      setErrorMessage("You can only upload up to 25 PDF files")
      return
    }

    if (selectedFiles.length > 0 && isConfirmed) {
      const invalidFiles = selectedFiles.filter((file) => {
        const allowedTypes = [
          "application/pdf",
          "image/tiff",
          "application/msword", // .doc
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
          "application/vnd.ms-excel", // .xls
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        ]
        return !allowedTypes.includes(file.type)
      })

      if (invalidFiles.length > 0) {
        setErrorMessage(
          "Only pdf, tif, tiff, doc, docx, xls, xlsx files are allowed",
        )
        return
      }

      const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0)
      if (totalSize > 100 * 1024 * 1024) {
        setErrorMessage("Total file size exceeds 100MB")
        return
      }

      if (selectedFiles.length > 25) {
        setErrorMessage("You can only upload up to 25 PDF files")
        return
      }
    }

    // if (includeCoverPage && (!fromName || !subject || !message)) {
    //   setErrorMessage("All cover page fields must be filled if included.")
    //   return
    // }

    if (selectedFiles.length === 0 && !isConfirmed) {
      setIsNoFileAttached(true)
      setErrorMessage("")
      return
    }

    const metadata = {
      destinations: [
        {
          to_name: toName,
          to_company: toCompany,
          fax_number: prepareFinalFaxNumber(faxNumber),
        },
      ],
      include_cover_page: includeCoverPage,
      coverPageOptions: includeCoverPage
        ? {
            from_name: includeCoverPage ? fromName : null,
            subject: includeCoverPage ? subject : null,
            message: includeCoverPage ? message : null,
          }
        : {},
      userId: toUserIdValue,
      comment1,
      comment2,
    }

    const appId = showComposePopup?.flgPersonalFax?.appId
      ? showComposePopup?.flgPersonalFax?.appId
      : faxNumberAttributes.find((app) => app.userId === toUserIdValue)?.appId

    // console.log("metadata", metadata)
    // console.log("appId", appId)
    // console.log("file", selectedFiles)
    // return

    const formData = new FormData()

    const blob = new Blob([JSON.stringify(metadata)], {
      type: "application/json",
    })
    formData.append("metadata", blob)

    // if no files selected, send test.pdf from public folder
    if (selectedFiles.length === 0) {
      setActionLoading(true)
      const response = await fetch("/default_doc.pdf")
      const pdfBlob = await response.blob()

      const file = new File([pdfBlob], "default_doc.pdf", {
        type: "application/pdf",
      })

      formData.append("documents", file)
    } else {
      selectedFiles.forEach((file) => {
        formData.append("documents", file) // name MUST include [] if backend expects it
      })
    }

    onSave(formData, appId, toUserId?.faxNumber, toUserId?.queueOrNetid)
  }

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)

    const combinedFiles = [...selectedFiles, ...files]

    setSelectedFiles(combinedFiles)
  }

  const handleRemoveFile = (indexToRemove) => {
    const newFiles = selectedFiles.filter((_, index) => index !== indexToRemove)
    setSelectedFiles(newFiles)
  }

  // const handleMessageChange = (value) => {
  //       const lines = value.split("\n")

  //       // Keep everything after first line editable
  //       const editablePart = lines.slice(1).join("\n")

  //       setMessage(
  //         `Fax Number: ${formatFaxNumber(toUserId?.faxNumber)}
  //   ${editablePart}`,
  //       )

  //   setMessage(value)
  // }

  const ToolTip = ({ size = 16, className = "" }) => {
    return (
      <span className={`tooltip-container ${className}`}>
        <Info
          size={size}
          color="grey"
          style={{
            verticalAlign: "middle",
            marginLeft: "5px",
            cursor: "pointer",
          }}
        />

        <span className="tooltip-text">
          Allowed pdf, tif, tiff, doc, docx, xls, xlsx.
          <br /> Max 25 files / 100MB.
        </span>
      </span>
    )
  }

  // useEffect(() => {
  //   handleMessageChange(message)
  // }, [toUserId, includeCoverPage])

  useEffect(() => {
    setErrorMessage("")
  }, [selectedFiles])

  useEffect(() => {
    if (showComposePopup) {
      // setFaxNumberAttributes(showComposePopup.faxNumbers)
      setFaxNumberAttributes(
        showComposePopup.faxNumbers.filter((f) => f.faxNumber && f.appId), // filter null appid or faxnumbers
      )
    }

    if (showComposePopup?.section !== "queue") {
      fetchDestinationFaxNumbers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComposePopup])

  const optionFaxNumberAttributes = faxNumberAttributes.map((fax) => ({
    value: fax.userId,
    label: `${fax.queueOrNetid} | ${formatFaxNumber(fax.faxNumber)}`,
    queueOrNetid: `${fax.queueOrNetid}`,
    faxNumber: fax.faxNumber,
  }))

  const optionDestinationFaxNumbers = destinationfaxNumbers.map((fax) => ({
    toName: `${fax.firstName} ${fax.lastName}`,
    toCompany: `${fax.company}`,
    value: fax.faxNumber,
    label: `${formatFaxNumber(fax.faxNumber)} | ${fax.firstName} ${
      fax.lastName
    } ${fax?.company ? `| ${fax?.company}` : ""}`,
  }))

  return (
    <div className="popup-overlay">
      <div className="popup" style={{ maxWidth: "800px" }}>
        {actionLoading ? (
          <div className="loading" style={{ padding: "20px" }}>
            In progress ...
          </div>
        ) : (
          <>
            <div className="checkbox-popup-header">
              <h3 className="popup-title">Compose Fax</h3>

              {!isNoFileAttached && (
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 24,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {!isNoFileAttached ? (
              <form
                className="popup-group form-container"
                onSubmit={(e) => handleSubmit(e, false)}
              >
                <div className="form-columns">
                  <div className="form-left">
                    {!showComposePopup?.flgPersonalFax && (
                      <div className="form-group">
                        <label htmlFor="email">
                          Origination Fax Number:{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>

                        <Select
                          id="toUserId"
                          className="basic-single"
                          classNamePrefix="select"
                          onChange={(selectedOption) => {
                            setToUserId(selectedOption)
                            if (showComposePopup?.section === "queue") {
                              fetchDestinationFaxNumbers(
                                selectedOption.queueOrNetid,
                              )
                            }
                          }}
                          placeholder="Select a fax number"
                          options={optionFaxNumberAttributes}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: 0,
                              textAlign: "left",
                            }),
                            option: (provided) => ({
                              ...provided,
                              textAlign: "left",
                            }),
                          }}
                          isSearchable
                        />
                      </div>
                    )}

                    {(toUserId || showComposePopup?.flgPersonalFax) && (
                      <div className="form-group">
                        <label htmlFor="email">
                          Destination Fax Number:{" "}
                          <span style={{ color: "red" }}>*</span>
                          <FaxNumberToolTip />
                        </label>

                        {destinationFaxNumberLoading ? (
                          <div style={{ textAlign: "left", padding: "10px" }}>
                            Loading...
                          </div>
                        ) : (
                          <>
                            <FaxSearchInput
                              faxNumber={faxNumber}
                              setFaxNumber={setFaxNumber}
                              setToName={setToName}
                              setToCompany={setToCompany}
                              optionDestinationFaxNumbers={
                                optionDestinationFaxNumbers
                              }
                            />
                          </>
                        )}
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="toName">To Name:</label>
                      <input
                        type="text"
                        id="toName"
                        value={toName}
                        onChange={(e) => setToName(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="toCompany">To Company:</label>
                      <input
                        type="text"
                        id="toCompany"
                        value={toCompany}
                        onChange={(e) => setToCompany(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="comment1">Comment1:</label>
                      <input
                        type="text"
                        id="comment1"
                        value={comment1}
                        onChange={(e) => setComment1(e.target.value)}
                        maxLength={45}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="comment2">Comment2:</label>
                      <input
                        type="text"
                        id="comment2"
                        value={comment2}
                        onChange={(e) => setComment2(e.target.value)}
                        maxLength={45}
                      />
                    </div>
                  </div>

                  <div class="form-right" style={{ marginLeft: "10px" }}>
                    <div className="form-group">
                      <label htmlFor="attachments">
                        Attach Files:
                        {/* <span style={{ color: "red" }}>*</span> */}
                        <ToolTip />
                        {/* <br />
                      <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                        Allowed pdf, tif, tiff, doc, docx, xls, xlsx.
                      </span>
                      <br />
                      <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                        Max 25 files / 100MB.
                      </span> */}
                      </label>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {/* Hidden real input */}
                        <input
                          type="file"
                          id="customFileInput"
                          accept="
                          application/pdf,
                          image/tiff,
                          .tif,.tiff,
                          .doc,.docx,
                          application/vnd.ms-excel,
                          application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                          .xls,.xlsx"
                          multiple
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />

                        {/* Custom trigger button */}
                        <label
                          htmlFor="customFileInput"
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#E5E5E5",
                            color: "black",
                            // borderRadius: "4px",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          Choose Files
                        </label>

                        {/* Dynamic file count */}
                        <span
                          style={{
                            marginBottom: "5px",
                          }}
                        >
                          {selectedFiles.length > 0
                            ? `${selectedFiles.length} file${
                                selectedFiles.length > 1 ? "s" : ""
                              }`
                            : "No files selected"}
                        </span>
                      </div>
                      {/* {selectedFiles.length > 0 && (
                  <span>
                    {selectedFiles.length} file
                    {selectedFiles.length !== 1 ? "s" : ""} selected
                  </span>
                )} */}
                      <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                        {selectedFiles.length > 0 && (
                          <ul>
                            {selectedFiles.map((file, index) => (
                              <li
                                key={index}
                                style={{
                                  textAlign: "left",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                {file.name} (
                                {(file.size / (1024 * 1024)).toFixed(2)} MB)
                                <div
                                  type="button"
                                  onClick={() => handleRemoveFile(index)}
                                  style={{
                                    marginLeft: "10px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <X size={14} color="red" />
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div
                      className="form-group checkbox-group"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                      }}
                    >
                      <div>
                        <input
                          type="checkbox"
                          id="includeCoverPage"
                          checked={includeCoverPage}
                          disabled={true}
                          onChange={(e) =>
                            setIncludeCoverPage(e.target.checked)
                          }
                          style={{ marginRight: "5px", width: "20px" }}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="includeCoverPage"
                          style={{
                            whiteSpace: "nowrap",
                            marginBottom: "0px",
                          }}
                        >
                          Include Cover Page
                        </label>
                      </div>
                    </div>

                    {includeCoverPage && (
                      <>
                        <div className="form-group">
                          <label htmlFor="fromName">From Name:</label>
                          <input
                            type="text"
                            id="fromName"
                            value={fromName}
                            onChange={(e) => setFromName(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="subject">Subject:</label>
                          <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="message">Message:</label>
                          <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            // onChange={(e) =>
                            //   handleMessageChange(e.target.value)
                            // }
                            style={{
                              width: "100%",
                              minHeight: "80px",
                              resize: "vertical",
                            }}
                          ></textarea>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {errorMessage && (
                  <div className="error-message">{errorMessage}</div>
                )}

                <div className="popup-buttons">
                  <button
                    type="button"
                    className="popup-button cancel"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="popup-button save">
                    Send
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{ padding: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <TriangleAlert size={18} fill="yellow" stroke="black" style={{ marginRight: "5px", verticalAlign: "middle" }} />
                  You haven't attached any files. Are you sure you want to send this fax?
                </div>
                <div className="popup-buttons" style={{ padding: "10px" }}>
                  <button
                    type="button"
                    className="popup-button cancel"
                    onClick={() => setIsNoFileAttached(false)}
                  >
                    No
                  </button>
                  <button
                    type="submit"
                    className="popup-button save"
                    onClick={(e) => handleSubmit(e, true)}
                  >
                    Yes, Send
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function FaxSearchInput({
  faxNumber,
  setFaxNumber,
  setToName,
  setToCompany,
  optionDestinationFaxNumbers,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [hoverIndex, setHoverIndex] = useState(null)

  // keep local query in sync with external faxNumber
  useEffect(() => {
    const v = typeof faxNumber === "string" ? faxNumber : faxNumber?.value || ""
    setQuery(v || "")
  }, [faxNumber])

  const filteredOptions = useMemo(() => {
    if (!query) return optionDestinationFaxNumbers || []
    const q = query.toLowerCase().trim().replace(/-/g, "")
    return (optionDestinationFaxNumbers || []).filter((opt) =>
      String(
        opt.label.trim().replace(/-/g, "") ||
          opt.value.trim().replace(/-/g, "") ||
          "",
      )
        .toLowerCase()
        .includes(q),
    )
  }, [query, optionDestinationFaxNumbers])

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        id="faxNumber"
        name="faxNumber"
        type="text"
        placeholder="Enter a fax number"
        value={query}
        onChange={(e) => {
          const v = e.target.value
          setQuery(v)

          // keep same shape as react-select: { value, label }
          setFaxNumber(v || null)

          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // allow click to register before closing
          setTimeout(() => setOpen(false), 150)
        }}
        autoComplete="off" // disable browser suggestions
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: 0,
        }}
      />

      {open && filteredOptions.length > 0 && (
        <ul class="fax-search-input-ul">
          {filteredOptions.map((opt, idx) => (
            <li
              key={opt.value}
              onMouseDown={(e) => {
                e.preventDefault() // prevent blur before click
                setQuery(formatFaxNumber(opt.value))

                setFaxNumber(formatFaxNumber(opt.value))
                // setToName(opt.toName)
                // setToCompany(opt.toCompany)
                setToName(isEmpty(opt.toName, true) ? "" : opt.toName)
                setToCompany(isEmpty(opt.toCompany, true) ? "" : opt.toCompany)

                setOpen(false)
              }}
              onMouseEnter={() => setHoverIndex(idx)}
              onMouseLeave={() => setHoverIndex(null)}
              style={{
                padding: "7px 10px",
                marginTop: idx === 0 ? "5px" : "0px",
                cursor: "pointer",
                backgroundColor:
                  hoverIndex === idx ? "#bedaf8ff" : "transparent", // 👈 hover effect
              }}
            >
              {opt.label || opt.value}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
